import { useContext } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { PartialMessage } from '@zg-rentals/backend-styled-components';
import { Box, Button, Heading, Paragraph, Popover } from '@zillow/constellation-10';
import { useClientAppConfigWebHandler_getFeature } from '@zg-rentals/rental-manager-env';
import { LoadingButton } from '@zg-rentals/loading-components';

import { RMWContext } from '../../../../providers/RMWContext';

import { useSelector } from '../../../../selectors/useSelector';
import { LoadingMapContext } from '../../../../providers/LoadingMapContext/context';

import accessUtils from '../../../../utils/accessUtils';

import { LISTING_SUMMARY_DISPLAY_STATUS } from '../../../../constants/ListingSummaryConstants';

import TenantPlacementUpsellBanner from '../../../TenantPlacementUpsellBanner';

import { StyledButtonContainer, StyledCTAComponent } from './StyledComponents';

import type { ReactElement } from 'react';
import { updateIsWizardView } from '../../../../slices/app';

type Props = {
  listingAlias: string;
  canResumeModularListingCreation?: boolean;
  status: string;
  canDeactivate: boolean;
  onDeactivateClick: () => void;
};

const getCTAPath = (
  listingAlias = '',
  paths = {},
  status = '',
  canResumeModularListingCreation = false,
  hasListingBeenActivated = false,
) => {
  // @ts-expect-error
  const { propertiesPage } = paths;

  if (canResumeModularListingCreation) {
    return `${propertiesPage}/${listingAlias}/create-listing`;
  }

  const listingPath =
    status !== LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE || hasListingBeenActivated ? 'listing' : 'create-listing';

  return `${propertiesPage}/${listingAlias}/${listingPath}`;
};

const CTA = ({ onDeactivateClick, canDeactivate, listingAlias, canResumeModularListingCreation, status }: Props) => {
  const { paths } = useContext(RMWContext);
  const {
    loadingByComponent: { ConnectedListing: isConnectedListingLoading },
  } = useContext(LoadingMapContext);
  const dispatch = useDispatch();

  const { active, currentProperty, dateLastActivatedTimestamp, isUpdateActiveStatusAllowed, partialMessages, user } =
    useSelector((state) => {
      return {
        active: state.properties.currentProperty.active,
        appConfig: state.app.appConfig,
        currentProperty: state.properties.currentProperty,
        dateLastActivatedTimestamp: state.properties.currentProperty.dateLastActivatedTimestamp,
        isUpdateActiveStatusAllowed: state.properties.currentProperty.updateActiveStatus.isAllowed,
        partialMessages: state.properties.currentProperty.updateActiveStatus.partialMessages,
        user: state.user,
      };
    }, shallowEqual);

  const { data: enabledStates = '' } = useClientAppConfigWebHandler_getFeature({
    appName: 'zrm_web',
    featureName: 'tenantPlacementStates',
  });

  const hasListingBeenActivated = dateLastActivatedTimestamp !== '';
  const ctaPath = getCTAPath(listingAlias, paths, status, canResumeModularListingCreation, hasListingBeenActivated);
  const ctaTitle = canResumeModularListingCreation ? 'Resume' : 'List your property';

  const isActiveAndUpdateActiveStatusNotAllowed = active && !isUpdateActiveStatusAllowed;

  const canAccessTenantPlacementListingModuleBanner = accessUtils.canAccessTenantPlacementListingModuleBanner({
    currentProperty,
    user,
    enabledStates,
  });

  const gotoEditListingPage = () => {
    if (canResumeModularListingCreation) dispatch(updateIsWizardView(true));

    window.router.transitionTo(ctaPath);
  };

  const handleDeactivateClick = () => {
    if (isActiveAndUpdateActiveStatusNotAllowed) {
      // do not fall through. we will display a popper
      return;
    }

    onDeactivateClick();
  };

  const withUnableToDeactivatePopper = (component: ReactElement) => {
    if (!component) {
      return null;
    }

    return (
      <Popover
        trigger={component}
        tone="info"
        header={<Heading level={6}>Unable to deactivate</Heading>}
        body={
          <Paragraph>
            <PartialMessage partialMessages={partialMessages} />
          </Paragraph>
        }
      />
    );
  };

  const renderDeactivateButton = () =>
    isConnectedListingLoading ? (
      <LoadingButton isLoading={isConnectedListingLoading} loadingWidth="50%" css={{ marginLeft: '300' }}>
        Deactivate
      </LoadingButton>
    ) : (
      <Button
        tone={isUpdateActiveStatusAllowed ? 'brand' : 'neutral'}
        emphasis={isUpdateActiveStatusAllowed ? 'filled' : 'outlined'}
        disabled={!canDeactivate}
        fluid={true}
        onClick={handleDeactivateClick}
        data-testid="deactivate-button"
        css={{ marginLeft: '300' }}
      >
        Deactivate
      </Button>
    );

  const renderCTA = () => {
    if (status === LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE) {
      return (
        <LoadingButton
          tone="brand"
          emphasis="filled"
          fluid={true}
          onClick={gotoEditListingPage}
          data-test={
            canResumeModularListingCreation ? 'connected-listing-resume-cta' : 'connect-listing-list-my-property-cta'
          }
          isLoading={isConnectedListingLoading}
          loadingWidth="100%"
        >
          {ctaTitle}
        </LoadingButton>
      );
    }

    return (
      <StyledButtonContainer aria-label="edit and deactivate">
        <LoadingButton
          fluid={true}
          onClick={gotoEditListingPage}
          isLoading={isConnectedListingLoading}
          loadingWidth="50%"
        >
          Edit
        </LoadingButton>
        {isActiveAndUpdateActiveStatusNotAllowed
          ? withUnableToDeactivatePopper(renderDeactivateButton())
          : renderDeactivateButton()}
      </StyledButtonContainer>
    );
  };

  return (
    <>
      <StyledCTAComponent className="CTA">{renderCTA()}</StyledCTAComponent>
      {canAccessTenantPlacementListingModuleBanner && (
        <Box css={{ marginTop: '400' }}>
          <TenantPlacementUpsellBanner />
        </Box>
      )}
    </>
  );
};

export default CTA;
